// src/hooks/useTelegramAuth.ts — обновлённая версия с fallback по #tgWebAppData и логами на сервер
import { useEffect, useRef, useState } from "react";

export type AuthState = "loading" | "authorized" | "forbidden" | "error";

/** То, что приходит с бэка */
export interface AdminInfoDTO {
    id: number;
    userId: string;
    locationId: number;
    adminBotId: number;
    clientBotId: number;
}

export interface TelegramUserInfoDTO {
    telegramUserId: number;
    username?: string;
    firstName?: string;
    lastName?: string;

    /** если админ найден */
    admin?: AdminInfoDTO | null;
}

type DebugEvent = { sid: string; event: string; ts: string; data?: any };

function safeJson(value: any) {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return String(value);
    }
}

function createSessionId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function postDebug(sid: string, event: string, data?: any) {
    // fire-and-forget
    fetch("/api/debug/log", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Debug-Sid": sid,
        },
        body: JSON.stringify({
            sid,
            events: [
                {
                    sid,
                    event,
                    ts: new Date().toISOString(),
                    data: safeJson(data),
                } as DebugEvent,
            ],
        }),
    }).catch(() => {});
}

function getInitDataFromHash(): string | null {
    try {
        const raw = window.location.hash || "";
        const i = raw.indexOf("tgWebAppData=");
        if (i < 0) return null;

        // hash is like: #tgWebAppData=....&tgWebAppVersion=...
        const after = raw.substring(i + "tgWebAppData=".length);
        const end = after.indexOf("&");
        const encoded = end >= 0 ? after.substring(0, end) : after;

        // tgWebAppData in hash is URL-encoded
        return decodeURIComponent(encoded);
    } catch {
        return null;
    }
}

function getWebAppInitData(): { initData: string | null; hasWebApp: boolean; webApp?: any } {
    const webApp = (window as any).Telegram?.WebApp;
    const initData = typeof webApp?.initData === "string" ? webApp.initData : null;
    return { initData, hasWebApp: !!webApp, webApp };
}

function preview(text: string | null | undefined, max = 200) {
    if (!text) return null;
    return text.length > max ? text.slice(0, max) + "…" : text;
}

export function useTelegramAuth() {
    const [state, setState] = useState<AuthState>("loading");

    /** пользователь из Telegram */
    const [user, setUser] = useState<TelegramUserInfoDTO | null>(null);

    /** admin info, если нашли (удобно для App.tsx) */
    const [admin, setAdmin] = useState<AdminInfoDTO | null>(null);

    // общий sid, чтобы по логам собрать весь поток
    const sidRef = useRef<string>(createSessionId());

    useEffect(() => {
        const sid = sidRef.current;

        const { initData: webInitData, hasWebApp, webApp } = getWebAppInitData();
        const hashInitData = getInitDataFromHash();

        postDebug(sid, "TG_AUTH_ENTER", {
            hasWebApp,
            webInitDataLen: webInitData?.length ?? null,
            hashInitDataLen: hashInitData?.length ?? null,
            href: window.location.href,
        });

        // если есть WebApp — вызываем ready/expand
        if (hasWebApp) {
            try {
                webApp?.ready?.();
                webApp?.expand?.();
                postDebug(sid, "TG_WEBAPP_READY_OK", {
                    platform: webApp?.platform ?? null,
                    version: webApp?.version ?? null,
                    colorScheme: webApp?.colorScheme ?? null,
                    hasUnsafeUser: !!webApp?.initDataUnsafe?.user,
                    unsafeUserId: webApp?.initDataUnsafe?.user?.id ?? null,
                });
            } catch (e: any) {
                postDebug(sid, "TG_WEBAPP_READY_FAIL", { error: e?.message ?? String(e) });
            }
        }

        const initData = webInitData || hashInitData;

        if (!initData) {
            postDebug(sid, "TG_AUTH_NO_INIT_DATA", { hasWebApp });
            setState("error");
            return;
        }

        postDebug(sid, "TG_AUTH_REQUEST", { initDataLen: initData.length });

        fetch("/api/auth/telegram", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ initData }),
        })
            .then(async (res) => {
                // читаем текст для логов, но JSON парсим отдельно
                const rawText = await res.text().catch(() => "");
                postDebug(sid, "TG_AUTH_RESPONSE", {
                    status: res.status,
                    ok: res.ok,
                    textPreview: preview(rawText, 250),
                });

                if (res.ok) {
                    let dto: TelegramUserInfoDTO | null = null;
                    try {
                        dto = rawText ? (JSON.parse(rawText) as TelegramUserInfoDTO) : null;
                    } catch (e: any) {
                        postDebug(sid, "TG_AUTH_JSON_PARSE_FAIL", { error: e?.message ?? String(e), textPreview: preview(rawText, 250) });
                        dto = null;
                    }

                    setUser(dto);
                    setAdmin(dto?.admin ?? null);
                    setState("authorized");

                    postDebug(sid, "TG_AUTH_OK", {
                        telegramUserId: dto?.telegramUserId ?? null,
                        hasAdmin: !!dto?.admin,
                        adminLocationId: dto?.admin?.locationId ?? null,
                    });

                    return;
                }

                // 403 — не админ (или не разрешён доступ)
                if (res.status === 403) {
                    setState("forbidden");
                    postDebug(sid, "TG_AUTH_FORBIDDEN");
                    return;
                }

                // 401/400/500 — ошибка/невалидный initData/проблема на бэке
                setState("error");
                postDebug(sid, "TG_AUTH_ERROR_STATUS", { status: res.status });
            })
            .catch((e) => {
                postDebug(sid, "TG_AUTH_FETCH_FAIL", { error: e?.message ?? String(e) });
                setState("error");
            });
    }, []);

    return { state, user, admin };
}
